import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowRight, Loader2, LogIn, ShieldCheck } from "lucide-react";
import MembersTable from "@/components/master/MembersTable";
import ResetAttemptsTable from "@/components/master/ResetAttemptsTable";

/**
 * Phase 36 — Master workspace detail page.
 *
 * Sections:
 *   • Header: tenant identity + "Enter workspace" button (reuses the
 *     existing POST /api/auth/switch-tenant primitive, same as
 *     TenantSwitcher.jsx — no new session protocol).
 *   • Settings / integrations / mail-diagnostics tiles — read-only.
 *   • MembersTable with search + role/status filters + per-member
 *     "Send reset link" action.
 *   • ResetAttemptsTable — the diagnostic surface from Phase 36.4.
 */
export default function MasterWorkspaceDetailPage() {
  const { tenant_id } = useParams();
  const navigate = useNavigate();
  const { switchTenant } = useAuth();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [entering, setEntering] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/master/tenants/${tenant_id}`);
      setDetail(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tenant_id]);

  const onEnter = async () => {
    if (!detail) return;
    setEntering(true);
    try {
      // Correction (Phase 36): reuse the existing switch-tenant flow
      // verbatim — same primitive used by TenantSwitcher.jsx. The
      // existing ImpersonationBanner (`frontend/src/components/
      // ImpersonationBanner.jsx`) auto-renders when `me.is_impersonating`
      // flips true server-side; no new banner is introduced here.
      await switchTenant({ tenant_id });
      toast.success(`Entered ${detail.name}`);
      navigate("/app/dashboard", { replace: true });
      setTimeout(() => window.location.reload(), 60);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setEntering(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center p-12" data-testid="master-detail-loading">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (!detail) {
    return (
      <div className="p-6" data-testid="master-detail-missing">
        <p className="text-sm text-muted-foreground">Workspace not found.</p>
        <Button variant="link" onClick={() => navigate("/master/tenants")}>
          Back to all workspaces
        </Button>
      </div>
    );
  }

  const authMode = detail.auth_mode || "native";
  const authBadge = {
    native: "bg-primary/10 text-primary",
    hybrid: "bg-secondary/10 text-secondary",
    federated: "bg-muted text-foreground",
  }[authMode] || "bg-muted text-foreground";

  return (
    <div className="space-y-8" data-testid="master-workspace-detail">
      {/* ------------------------------------------------- Header */}
      <div className="flex flex-col gap-4 border-b pb-6 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">
            Workspace
          </div>
          <h1 className="mt-1 font-display text-3xl font-semibold text-primary" data-testid="master-detail-name">
            {detail.name}
          </h1>
          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            <code className="rounded bg-muted px-2 py-0.5 font-mono text-xs">
              {detail.slug}
            </code>
            <Badge className={authBadge} data-testid="master-detail-auth-mode">
              {authMode}
            </Badge>
            <Badge variant="outline" data-testid="master-detail-status">
              {detail.status}
            </Badge>
            <span className="text-xs">{detail.timezone}</span>
          </div>
        </div>
        <Button
          onClick={onEnter}
          disabled={entering}
          data-testid="master-enter-workspace"
          className="shrink-0"
        >
          {entering ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <LogIn className="mr-2 h-4 w-4" />
          )}
          Enter workspace
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>

      {/* -------------------------------------- Integration tiles */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <IntegrationTile
          testId="int-federation"
          title="Federation"
          configured={!!detail.integrations?.federation?.configured}
          enabled={!!detail.integrations?.federation?.enabled}
          extras={[
            ["Mode", detail.integrations?.federation?.auth_mode || "native"],
            ["Algorithm", detail.integrations?.federation?.algorithm || "—"],
            ["HS256 secret", detail.integrations?.federation?.has_hs256_secret ? "present" : "none"],
            ["RS256 public key", detail.integrations?.federation?.has_rs256_public_key ? "present" : "none"],
          ]}
        />
        <IntegrationTile
          testId="int-pace"
          title="Pace (AI nudges)"
          configured={!!detail.integrations?.pace?.configured}
          enabled={!!detail.integrations?.pace?.enabled}
          extras={[
            ["Model", detail.integrations?.pace?.model || "—"],
            ["Last error", detail.integrations?.pace?.last_error_category || "none"],
          ]}
        />
        <IntegrationTile
          testId="int-connecteam"
          title="Connecteam"
          configured={!!detail.integrations?.connecteam?.configured}
          enabled={!!detail.integrations?.connecteam?.enabled}
        />
        <IntegrationTile
          testId="int-scim"
          title="SCIM 2.0"
          configured={!!detail.integrations?.scim?.configured}
          enabled={!!detail.integrations?.scim?.enabled}
        />
        <Card data-testid="int-mail">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-sm">
              <ShieldCheck className="h-4 w-4 text-secondary" />
              Mail diagnostics
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <Row label="Sender domain" value={detail.mail?.mail_from_domain || "Not configured"} />
            <Row label="Mail driver" value={detail.mail?.mail_driver} />
            <Row label="Last outbound ok" value={fmtTs(detail.mail?.last_outbound_ok_at)} />
            <Row label="Last outbound fail" value={fmtTs(detail.mail?.last_outbound_fail_at)} />
            <Row label="Last fail category" value={detail.mail?.last_fail_category || "—"} />
          </CardContent>
        </Card>
        <Card data-testid="tile-time-config">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Time entry config</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-xs">
            <Row label="Worker duration mode" value={detail.allow_worker_duration_entry ? "allowed" : "clock in/out only"} />
            <Row label="Max daily minutes" value={detail.max_daily_minutes} />
          </CardContent>
        </Card>
      </div>

      <Separator />

      {/* ---------------------------------------------- Members */}
      <section data-testid="master-detail-members-section">
        <div className="mb-3 flex items-end justify-between">
          <div>
            <h2 className="font-display text-lg font-semibold text-primary">Members</h2>
            <p className="text-xs text-muted-foreground">
              Search members or send a password reset link.
            </p>
          </div>
        </div>
        <MembersTable tenantId={tenant_id} tenantSlug={detail.slug} />
      </section>

      <Separator />

      {/* -------------------------------------- Reset attempts */}
      <section data-testid="master-detail-attempts-section">
        <div className="mb-3">
          <h2 className="font-display text-lg font-semibold text-primary">Password-reset attempts</h2>
          <p className="text-xs text-muted-foreground">
            Recent password reset requests and email sending results.
          </p>
        </div>
        <ResetAttemptsTable tenantId={tenant_id} />
      </section>
    </div>
  );
}

function IntegrationTile({ testId, title, configured, enabled, extras = [] }) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-sm">
          <span>{title}</span>
          <Badge
            variant={configured ? "default" : "outline"}
            className={configured ? "bg-secondary/15 text-secondary" : ""}
          >
            {configured ? (enabled ? "enabled" : "configured") : "off"}
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-1 text-xs">
        {extras.map(([k, v]) => <Row key={k} label={k} value={v} />)}
      </CardContent>
    </Card>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className="truncate font-medium text-foreground">{String(value ?? "—")}</span>
    </div>
  );
}

function fmtTs(v) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(); } catch (_e) { return String(v); }
}
